<template>
    <div>
        <!-- Full-screen loading overlay -->
        <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="64"
                    color="primary"
                ></v-progress-circular>
                <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
            </div>
        </v-overlay>

        <!-- Block Tạo License -->
        <v-card class="mb-6">
            <v-card-title class="text-h6 d-flex align-center">
                <v-icon class="mr-2">mdi-key-plus</v-icon>
                Tạo License Mới
            </v-card-title>
            <v-card-text>
                <v-form @submit.prevent="createLicense" ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-autocomplete
                                v-model="form.product_id"
                                :items="products"
                                item-title="name"
                                item-value="id"
                                label="Chọn sản phẩm"
                                placeholder="Tìm kiếm sản phẩm..."
                                prepend-inner-icon="mdi-package-variant"
                                :rules="[v => !!v || 'Vui lòng chọn sản phẩm']"
                                required
                                density="comfortable"
                                variant="outlined"
                            ></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.email"
                                label="Email khách hàng"
                                placeholder="Nhập email..."
                                prepend-inner-icon="mdi-email"
                                type="email"
                                :rules="[
                                    v => !!v || 'Vui lòng nhập email',
                                    v => /.+@.+\..+/.test(v) || 'Email không hợp lệ'
                                ]"
                                required
                                density="comfortable"
                                variant="outlined"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="form.expired_at"
                                label="Ngày hết hạn (Tuỳ chọn)"
                                type="date"
                                prepend-inner-icon="mdi-calendar"
                                clearable
                                density="comfortable"
                                variant="outlined"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" class="d-flex justify-end">
                            <v-btn
                                color="primary"
                                type="submit"
                                :loading="loading"
                                :disabled="!valid"
                                prepend-icon="mdi-plus"
                            >
                                Tạo License
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- Block Danh sách License -->
        <v-card>
            <v-card-title class="text-h6 d-flex align-center justify-space-between flex-wrap">
                <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-license</v-icon>
                    Danh sách License
                </div>
                <div class="d-flex gap-2" style="width: 100%; max-width: 400px; margin-top: 10px;">
                    <v-text-field
                        v-model="search"
                        label="Tìm kiếm license..."
                        prepend-inner-icon="mdi-magnify"
                        density="compact"
                        variant="outlined"
                        hide-details
                        class="flex-grow-1"
                        @input="onSearchInput"
                    ></v-text-field>
                </div>
            </v-card-title>
            <v-card-text>
                <v-table class="elevation-1" hover>
                    <thead>
                        <tr>
                            <th>Key License</th>
                            <th>Sản phẩm</th>
                            <th>Email</th>
                            <th>Ngày hết hạn</th>
                            <th class="text-center">Trạng thái</th>
                            <th class="text-center">Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="licenses.length === 0">
                            <td colspan="6" class="text-center py-4 text-grey">
                                Không có dữ liệu license
                            </td>
                        </tr>
                        <tr v-for="item in licenses" :key="item.id">
                            <td>
                                <div class="font-weight-medium text-primary">
                                    {{ item.key_license }}
                                    <v-btn icon size="x-small" variant="text" @click="copyToClipboard(item.key_license)">
                                        <v-icon size="14">mdi-content-copy</v-icon>
                                        <v-tooltip activator="parent" location="top">Sao chép</v-tooltip>
                                    </v-btn>
                                </div>
                            </td>
                            <td>{{ getProductName(item.product_id) }}</td>
                            <td>{{ item.email }}</td>
                            <td>
                                <span :class="isExpired(item.expired_at) ? 'text-error font-weight-bold' : ''">
                                    {{ item.expired_at ? formatDate(item.expired_at) : 'Vĩnh viễn' }}
                                </span>
                            </td>
                            <td class="text-center">
                                <v-chip
                                    :color="item.status === 1 ? 'success' : 'default'"
                                    size="small"
                                    variant="elevated"
                                >
                                    {{ item.status === 1 ? 'Hoạt động' : 'Không hoạt động' }}
                                </v-chip>
                            </td>
                            <td class="text-center">
                                <v-btn
                                    size="small"
                                    :color="item.status === 1 ? 'warning' : 'success'"
                                    variant="outlined"
                                    :loading="updatingStatus === item.id"
                                    @click="toggleStatus(item)"
                                >
                                    <v-icon start size="16">
                                        {{ item.status === 1 ? 'mdi-cancel' : 'mdi-check-circle' }}
                                    </v-icon>
                                    {{ item.status === 1 ? 'Hủy' : 'Kích hoạt' }}
                                </v-btn>
                            </td>
                        </tr>
                    </tbody>
                </v-table>
                
                <div class="d-flex justify-end mt-4" v-if="totalPages > 1">
                    <v-pagination
                        v-model="page"
                        :length="totalPages"
                        @update:modelValue="onPageChange"
                        rounded="circle"
                    ></v-pagination>
                </div>
            </v-card-text>
        </v-card>

        <!-- Snackbar notification -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
            {{ snackbarText }}
            <template v-slot:actions>
                <v-btn color="white" variant="text" @click="snackbar = false">Đóng</v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import axios from '@/http';

export default {
    name: 'License',
    data() {
        return {
            valid: false,
            pageLoading: false,
            loading: false, // create btn loading
            updatingStatus: null, // id of item being updated
            
            // Data
            products: [], // List for select
            licenses: [], // List for table
            
            // Form
            form: {
                product_id: null,
                email: '',
                expired_at: null
            },
            
            // Pagination & Search
            page: 1,
            limit: 10,
            search: '',
            totalPages: 1,
            
            // Snackbar
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
            
            _searchTimeout: null
        }
    },
    methods: {
        // Fetch list of products for the dropdown
        async fetchProducts() {
            try {
                // Get all products (limit -1 or large number) to populate select
                // Assuming API supports fetching all or we use pagination with large limit
                const res = await axios.get('/products', { params: { limit: 1000 } });
                this.products = res.data.data || [];
            } catch (e) {
                console.error('Lỗi tải danh sách sản phẩm', e);
                this.showSnackbar('Không thể tải danh sách sản phẩm', 'error');
            }
        },

        // Fetch licenses with pagination and search
        async fetchLicenses() {
            this.pageLoading = true;
            try {
                const res = await axios.get('/admin/license', {
                    params: {
                        page: this.page,
                        limit: this.limit,
                        search: this.search
                    }
                });
                this.licenses = res.data.data || [];
                // Assuming API returns pagination info like this, adjust if needed
                this.totalPages = res.data.totalPages || 1;
                // If API structure is different (e.g. flat array), handle accordingly
            } catch (e) {
                console.error('Lỗi tải danh sách license', e);
                this.showSnackbar('Không thể tải danh sách license', 'error');
            } finally {
                this.pageLoading = false;
            }
        },

        async createLicense() {
            if (!this.$refs.form.validate()) return;
            
            this.loading = true;
            try {
                await axios.post('/admin/license', this.form);
                this.showSnackbar('Tạo license thành công', 'success');
                
                // Reset form
                this.form = {
                    product_id: null,
                    email: '',
                    expired_at: null
                };
                if (this.$refs.form) this.$refs.form.resetValidation();
                
                // Reload list
                this.page = 1;
                this.fetchLicenses();
            } catch (e) {
                console.error('Lỗi tạo license', e);
                const msg = e.response?.data?.message || 'Tạo license thất bại';
                this.showSnackbar(msg, 'error');
            } finally {
                this.loading = false;
            }
        },

        async toggleStatus(item) {
            this.updatingStatus = item.id;
            const newStatus = item.status === 1 ? 0 : 1;
            try {
                await axios.put(`/admin/license/${item.id}/status`, { status: newStatus });
                item.status = newStatus;
                this.showSnackbar('Cập nhật trạng thái thành công', 'success');
            } catch (e) {
                console.error('Lỗi cập nhật trạng thái', e);
                this.showSnackbar('Cập nhật trạng thái thất bại', 'error');
            } finally {
                this.updatingStatus = null;
            }
        },

        // Helpers
        getProductName(productId) {
            const product = this.products.find(p => p.id === productId);
            return product ? product.name : `Sản phẩm #${productId}`;
        },
        
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('vi-VN');
        },

        isExpired(dateString) {
            if (!dateString) return false;
            const date = new Date(dateString);
            return date < new Date();
        },

        showSnackbar(text, color) {
            this.snackbarText = text;
            this.snackbarColor = color;
            this.snackbar = true;
        },

        onSearchInput() {
            clearTimeout(this._searchTimeout);
            this._searchTimeout = setTimeout(() => {
                this.page = 1;
                this.fetchLicenses();
            }, 400);
        },

        onPageChange(newPage) {
            this.page = newPage;
            this.fetchLicenses();
        },
        
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.showSnackbar('Đã sao chép vào clipboard', 'success');
            } catch (err) {
                this.showSnackbar('Lỗi sao chép', 'error');
            }
        }
    },
    mounted() {
        this.fetchProducts();
        this.fetchLicenses();
    }
}
</script>

<style scoped>
.gap-2 {
    gap: 8px;
}
</style>
